// Tag-oldal (spec 6.1): egy címke összes cikke, terület szerint csoportosítva.
import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Skeleton } from '@/components/ui/skeleton';
import { useKnowledgeTag } from '@/features/knowledge/api';
import { KnowledgeLayout } from '@/features/knowledge/components/knowledge-layout';
import { requireAuth } from '@/lib/auth';

export const Route = createFileRoute('/tag/$tag')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: TagPage,
});

function TagPage(): JSX.Element {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const query = useKnowledgeTag(tag);

  const body = query.data?.status === 200 ? query.data.body : null;

  return (
    <KnowledgeLayout showTree={false}>
      <KnowledgeCrumbs current={t('knowledge.tagTitle', { tag })} />

      <h1 className="text-read-2xl font-semibold tracking-tight">{t('knowledge.tagTitle', { tag })}</h1>
      <p className="text-muted-foreground text-read-sm mt-2">{t('knowledge.tagSubtitle')}</p>

      {query.isLoading && <Skeleton className="mt-8 h-40 w-full" />}

      {body?.groups.length === 0 && (
        <p className="text-muted-foreground mt-8 text-sm">{t('knowledge.tagEmpty')}</p>
      )}

      {!query.isLoading && !body && (
        <p className="text-destructive mt-8 text-sm">{t('knowledge.loadError')}</p>
      )}

      <div className="mt-8 space-y-10">
        {body?.groups.map((group) => (
          <section key={group.space.slug}>
            <h2 className="border-border border-b pb-2 text-lg font-semibold">
              <Link
                to="/hub/$spaceSlug"
                params={{ spaceSlug: group.space.slug }}
                className="hover:text-primary transition-colors duration-150"
              >
                {group.space.name}
              </Link>
            </h2>
            <ul className="mt-3 space-y-3">
              {group.articles.map((article) => (
                <li key={article.id}>
                  <Link
                    to="/hub/$spaceSlug/$categorySlug/$articleSlug"
                    params={{
                      spaceSlug: group.space.slug,
                      categorySlug: article.categorySlug,
                      articleSlug: article.slug,
                    }}
                    className="group block"
                  >
                    <span className="text-foreground group-hover:text-primary font-medium transition-colors duration-150">
                      {article.title}
                    </span>
                    <span className="text-muted-foreground mt-0.5 block text-sm">
                      {article.categoryName}
                      {article.summary ? ` · ${article.summary}` : ''}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </KnowledgeLayout>
  );
}
